import React, { useState, useEffect } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { debounce } from "lodash";
import { useAppContext } from "../../AppContext";
import searchIcon from "../../assets/search.svg";
import productRoute from "../../services/productRoute";

const normalizeProducts = (instruction) => {
  const raw = instruction?.products || instruction?.productIds || [];
  return raw.map((product) =>
    typeof product === "string"
      ? { id: product, productName: product }
      : { ...product, id: product?.id || product?._id }
  );
};

const InstructionModal = ({
  showInstructionModal,
  setShowInstructionModal,
  instruction = null,
  withProducts = false,
  onSaved = null,
}) => {
  const { showToast } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [newInstructionName, setNewInstructionName] = useState("");
  const [newInstructionAmount, setNewInstructionAmount] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [globalProducts, setGlobalProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const {
    addShoppingInstruction,
    updateShoppingInstruction,
    getSingleShoppingInstruction,
    getGlobalProducts,
  } = productRoute();

  const editingId = instruction?.id || instruction?._id;
  const isEditing = Boolean(editingId);

  useEffect(() => {
    if (!showInstructionModal) return;

    setNewInstructionName(instruction?.name || "");
    setNewInstructionAmount(
      instruction?.amount || instruction?.amount === 0 ? String(instruction.amount) : ""
    );
    setSelectedProducts(normalizeProducts(instruction));
    setSearchProduct("");

    if (isEditing && withProducts) {
      getSingleShoppingInstruction(editingId, setDetailsLoading, showToast, (fullInstruction) =>
        setSelectedProducts(normalizeProducts(fullInstruction))
      );
    }
  }, [showInstructionModal, editingId]);

  useEffect(() => {
    if (!showInstructionModal || !withProducts) return;

    const debouncedGetProducts = debounce((term) => {
      getGlobalProducts(setProductsLoading, showToast, setGlobalProducts, term, 1000);
    }, 500);

    if (searchProduct !== "") {
      debouncedGetProducts(searchProduct);
    } else {
      getGlobalProducts(setProductsLoading, showToast, setGlobalProducts, "", 1000);
    }

    return () => {
      debouncedGetProducts.cancel();
    };
  }, [showInstructionModal, searchProduct]);

  const selectedIds = selectedProducts.map((product) => product.id);

  const availableProducts = (globalProducts || []).filter(
    (product) => !selectedIds.includes(product?.id || product?._id)
  );

  const handleSelectProduct = (product) => {
    setSelectedProducts((prevProducts) => [
      ...prevProducts,
      { ...product, id: product?.id || product?._id },
    ]);
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prevProducts) =>
      prevProducts.filter((product) => product.id !== productId)
    );
  };

  const handleClose = () => {
    setShowInstructionModal(false);
  };

  const handleSaveInstruction = () => {
    const body = {
      name: newInstructionName,
      amount: parseFloat(newInstructionAmount) || 0, // Ensure amount is treated as a number
    };

    if (withProducts) {
      body.productIds = selectedIds;
    }

    if (isEditing) {
      updateShoppingInstruction(
        editingId,
        body,
        setLoading,
        showToast,
        () => {
          setShowInstructionModal(false);
          if (onSaved) onSaved();
        },
        "Shopping instruction updated"
      );
    } else {
      addShoppingInstruction(
        body,
        setShowInstructionModal,
        setLoading,
        showToast,
        onSaved
          ? () => {
              setShowInstructionModal(false);
              onSaved();
            }
          : null
      );
    }
  };

  const productLabel = (product) =>
    `${product?.productName}${product?.size ? ` - ${product?.size}` : ""}`;

  return (
    <Modal
      show={showInstructionModal}
      onHide={handleClose}
      centered
      size={withProducts ? "lg" : undefined}
    >
      <Modal.Header closeButton>
        {withProducts && (
          <h5 className="mb-0">{isEditing ? "Edit Instruction" : "Add Instruction"}</h5>
        )}
      </Modal.Header>
      {loading ? (
        <div
          className="justify-content-center align-items-center text-center"
          style={{ padding: "100px" }}
        >
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <Modal.Body className={withProducts ? "px-4 pb-4" : "px-5 pb-5"}>
            <Form>
              <Form.Group controlId="instructionName">
                <Form.Label>Instruction Name</Form.Label>
                <Form.Control
                  type="text"
                  value={newInstructionName}
                  onChange={(e) => setNewInstructionName(e.target.value)}
                  placeholder="Enter instruction name"
                />
              </Form.Group>
              <Form.Group controlId="instructionAmount" className="mt-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="text"
                  value={newInstructionAmount}
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "");
                    setNewInstructionAmount(numericValue);
                  }}
                  placeholder="Enter amount"
                />
              </Form.Group>
            </Form>

            {withProducts && (
              <div className="mt-4">
                <Form.Label>Products ({selectedProducts.length})</Form.Label>
                {detailsLoading ? (
                  <div className="text-center" style={{ padding: "20px" }}>
                    <div className="spinner-border spinner-border-sm text-success" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : selectedProducts.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {selectedProducts.map((product) => (
                      <span
                        key={product?.id}
                        className="d-flex align-items-center gap-2 px-3 py-1"
                        style={{
                          borderRadius: "20px",
                          fontSize: "14px",
                          border: "1px solid #4F4F4F4D",
                          backgroundColor: "#EAEAEA33",
                          color: "#333333",
                          fontWeight: "500",
                        }}
                      >
                        {productLabel(product)}
                        <span
                          className="px-1"
                          style={{
                            cursor: "pointer",
                            borderRadius: "50%",
                            border: "1px solid #333333",
                            lineHeight: "1",
                            fontSize: "12px",
                          }}
                          onClick={() => handleRemoveProduct(product?.id)}
                        >
                          x
                        </span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mb-3" style={{ color: "#667085", fontWeight: "500" }}>
                    No products selected yet.
                  </p>
                )}

                <div
                  className="d-flex align-items-center position-relative search-bar py-1 px-1 mb-2"
                  style={{ border: "1px solid #D9D9D9", fontWeight: "500" }}
                >
                  {searchProduct === "" && (
                    <img
                      src={searchIcon}
                      alt="search"
                      style={{ top: "24%", left: "1.5%" }}
                      className="position-absolute"
                    />
                  )}
                  <Form.Control
                    style={{ border: "none", backgroundColor: "transparent" }}
                    placeholder="Search products to add"
                    className="w-100"
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                  />
                </div>
                {productsLoading ? (
                  <div className="text-center" style={{ padding: "40px" }}>
                    <div className="spinner-border text-success" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : availableProducts.length > 0 ? (
                  <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {availableProducts.map((product) => {
                      const productId = product?.id || product?._id;
                      return (
                        <Form.Check
                          key={productId}
                          type="checkbox"
                          className="py-2"
                          id={`instruction-product-${productId}`}
                          label={productLabel(product)}
                          checked={false}
                          onChange={() => handleSelectProduct(product)}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <p className="my-3" style={{ color: "#667085", fontWeight: "500" }}>
                    No products available to add.
                  </p>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveInstruction}
              disabled={newInstructionName === ""}
            >
              {isEditing ? "Save Changes" : "Add Instruction"}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default InstructionModal;
